<template>
  <v-card>
    <v-card-title>
      {{$t(`incidents.${this.kind}_modal_title`)}}
    </v-card-title>

    <v-form @submit.prevent="onSubmit" ref="form" v-model="valid">
      <v-card-text>
        <v-container>

          <!-- INCIDENT TITLE -->
          <v-text-field
            :label='$t("common.name")'
            :rules="[rules.required, rules.lengthLimit(editable.title, 45)]"
            aria-required="true"
            counter="45"
            v-model="editable.title"
          />

          <!-- INCIDENT DESC -->
          <v-textarea
            :label='$t("common.description")'
            :rules="[rules.required]"
            v-model="editable.description"
          />

          <!-- INCIDENT DATE -->
          <DatetimePicker
            :allowedDates="allowedDates"
            :label="$t('common.created_for')"
            v-model="editable.created_for"
          />

            <!-- File input -->
            <AssetUploader
                v-if="kind === 'create'"
                v-model="editable.asset"
            />

          <!-- INCIDENT THEME -->
          <v-select
            :disabled="kind === 'update'"
            :items="availableThemes"
            :label='$t("common.theme")'
            :readonly="kind === 'update'"
            :rules="[rules.required]"
            @change="updateLevelsList"
            item-text="nameWithSector"
            item-value="id"
            v-if="!hasCurrentTheme"
            v-model="editable.theme_id"
            menu-props="auto"
          />

          <!-- INCIDENT LVL -->
          <v-select
            :items="levels"
            :label='$t("common.level")'
            :rules="[rules.required]"
            item-text="name"
            item-value="id"
            v-if="this.levels.length"
            v-model="editable.level_id"
            menu-props="auto"
          />

          <!-- INCIDENT ASSIGNED TO -->
          <v-select
            v-if="users.length > 0"
            :items="users"
            :label='$t("common.assigned_users")'
            item-text="name"
            item-value="id"
            v-model="editable.user_ids"
            menu-props="auto"
            multiple
            chips
          />


          <!-- INCIDENT WATCHERS -->
          <v-select
            v-if="users.length > 0"
            :items="users"
            :label='$t("common.watchers")'
            item-text="name"
            item-value="id"
            v-model="editable.watcher_ids"
            menu-props="auto"
            multiple
            chips
          />

        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="onReset" icon v-if="kind === 'create'">
          <v-icon>
            mdi-reload
          </v-icon>
        </v-btn>
        <v-spacer/>
        <v-btn @click="onCancel" color="waring" text> {{$t('common.cancel')}}</v-btn>
        <v-btn :disabled="!valid" color="primary" type="submit">
          {{$t(`common.${kind}`)}}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
    get, has, upperFirst,
} from 'lodash';
import { incidentFactory } from '@/core/incidentFactory';
import { required, lengthLimit } from '@/core/validators';
import DatetimePicker from '@/common/_datetimePicker.vue';
import moment from 'moment';
import AssetUploader from '@/common/assetUploader/AssetUploader.vue';

export default {
    name: 'incident-form',
    components: { DatetimePicker, AssetUploader },
    props: {
        users: {
            type: Array,
            required: false,
            default: () => ([]),
        },
        kind: {
            type: String,
            required: true,
            default: 'create',
        },
        incident: {
            type: Object,
            required: true,
            default: incidentFactory,
        },
        themes: {
            type: Array,
            required: true,
            default: () => ([]),
        },
    },
    data() {
        return {
            rules: {
                required,
                lengthLimit,
            },
            valid: true,
            editable: incidentFactory(),
        };
    },
    computed: {
        ...mapState('incidents', {
            levels: state => state.levels,
        }),
        selectedTheme() {
            return this.editable.theme_id;
        },
        hasCurrentTheme() {
            return get(this.incident, 'theme.id', '')
                .trim().length > 0;
        },
        availableThemes() {
            return this.themes.map(t => ({
                ...t,
                sector: this.assignedThemeSector(t),
                nameWithSector: `${t.name} - ${upperFirst(this.assignedThemeSector(t).name)} ${this.$t(
                    `common.${this.assignedSectorKind(t)}`,
                )}`,
            }));
        },
    },
    methods: {
        allowedDates: val => moment(val).isBetween(
            moment().subtract(7, 'day'),
            moment().add(1, 'day'),
            'day',
        ),
        assignedSectorKind(theme) {
            if (has(theme, 'workstation.id')) {
                return 'workstation';
            }

            if (has(theme, 'workplace.id')) {
                return 'workplace';
            }
            return 'site';
        },
        assignedThemeSector(theme) {
            return theme[this.assignedSectorKind(theme)];
        },
        updateLevelsList() {
            if (this.selectedTheme) this.fetchLevels(this.selectedTheme);
        },
        onSubmit() {
            if (this.$refs.form.validate()) {
                this.$emit('submit', this.editable);
            }
        },
        onReset() {
            this.$refs.form.resetValidation();
            this.editable = incidentFactory(this.incident);
            this.flushLevels();
        },
        onCancel() {
            this.$emit('cancel');
        },
        ...mapActions('incidents', ['fetchLevels', 'flushLevels']),
    },
    mounted() {
        this.flushLevels();
        this.editable = incidentFactory(this.incident);
        this.updateLevelsList();
    },
    watch: {
        incident(newIncident) {
            this.flushLevels();
            this.$refs.form.resetValidation();
            this.editable = incidentFactory(newIncident);
            this.updateLevelsList();
        },
    },
};
</script>

<style scoped>

</style>
